import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  RefObject,
} from "react";
import { IPanelScope, ITreeItem } from "@/interfaces";
import { BareButton, FilterListMessage, Loading } from "@/shared-components";
import { GENERIC_ERROR_MESSAGE, MIN_CRITERIA_LENGTH } from "@/constants";
import {
  ListItemRow,
  CollapseTreeButton,
} from "@/shared-components/PtTree/components";
import { ListTypes } from "@/enums";
import { MuxToggle } from "@/Icons";
import { FavoriteItem, PanelSettings } from "@/types";
import { FavoriteContextMenu } from "./components";
import { useFavoritesContext, FiltersContext } from "@/contexts";
import styles from "./FavoritePanel.module.scss";
import { getTranslation } from "@/utils";
import { findNodeById } from "@/utils/common";
import { FiltersService, FoldersService } from "@/services";
import { FOLDERS_TAB_KEY } from "@/constants";

interface FavoritePanelProps {
  scope: IPanelScope;
  favoriteRef: RefObject<HTMLUListElement>;
  isOpen: boolean;
  items: ITreeItem[];
  criteria: string;
  selectedItem: ITreeItem | null;
  settings: PanelSettings;
  setOpenState: (state: boolean) => void;
  handleTreeItemClick: (favorite: ITreeItem) => void;
}

export const FavoritePanel: React.FC<FavoritePanelProps> = ({
  scope,
  criteria,
  settings,
  isOpen,
  items,
  selectedItem,
  setOpenState,
  favoriteRef,
  handleTreeItemClick,
}) => {
  const {
    favorites,
    reloadFavorites,
    toggleFavorite,
    saveFavorites,
    renameFavorite,
  } = useFavoritesContext();
  const filtersContext = useContext(FiltersContext);

  const hasFetchedRef = useRef(false);

  const [visibleItems, setVisibleItems] = useState<FavoriteItem[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [editableId, setEditableId] = useState<string | null>(null);
  const storageKey = `fav-tree-${scope.tab}-${scope.userId}-${scope.module}-${scope.projectId}`;

  const loadExpandedNodes = (): Set<string> => {
    const stored = localStorage.getItem(storageKey);
    return stored ? new Set(stored.split(",")) : new Set();
  };

  const [expandedNodes, setExpandedNodes] =
    useState<Set<string>>(loadExpandedNodes);

  const saveExpandedNodes = (nodes: Set<string>) => {
    nodes.size
      ? localStorage.setItem(storageKey, [...nodes].join(","))
      : localStorage.removeItem(storageKey);
  };

  const cleanupStaleExpandedNodes = (
    favorites: FavoriteItem[],
    expandedNodes: Set<string>,
  ): Set<string> => {
    const favoriteIds = favorites.map((f) => String(f.id));
    const valid = new Set(
      [...expandedNodes].filter((key) =>
        favoriteIds.some(
          (id) => key === `root/${id}` || key.startsWith(`root/${id}/`),
        ),
      ),
    );
    if (valid.size !== expandedNodes.size) {
      setExpandedNodes(valid);
      saveExpandedNodes(valid);
    }
    return valid;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await reloadFavorites();
      } catch (e) {
        console.error(`FavoritePanel->reloadFavorites: ${e}`);
        setError(GENERIC_ERROR_MESSAGE);
      } finally {
        hasFetchedRef.current = true;
        setLoading(false);
      }
    })();
  }, []);

  // Restore expanded nodes from localStorage by fetching their children
  const favoritesKey = favorites.map((f) => f.id).join(",");
  useEffect(() => {
    if (!hasFetchedRef.current) return;
    const validExpanded = cleanupStaleExpandedNodes(favorites, expandedNodes);
    if (!validExpanded.size) return;

    const restoreExpandedChildren = async (
      items: FavoriteItem[],
      path: string,
    ) => {
      for (const item of items) {
        const nodeKey = `${path}/${item.id}`;
        if (validExpanded.has(nodeKey)) {
          item.children = [];
          await handleNodeExpand(item);
          if (item.children?.length) {
            await restoreExpandedChildren(item.children, nodeKey);
          }
        }
      }
    };

    restoreExpandedChildren(favorites, "root").then(() => {
      setVisibleItems([...favorites]);
    });
  }, [favoritesKey]);

  useEffect(() => {
    if (criteria.length > MIN_CRITERIA_LENGTH) {
      setVisibleItems(
        favorites.filter((f) =>
          f.name.toLowerCase().includes(criteria.toLowerCase()),
        ),
      );
    } else {
      setVisibleItems(favorites);
    }
  }, [criteria, favorites]);

  const onFavoriteClick = async (f: FavoriteItem) => {
    mixpanel.track("favorites", { Action: "click-on-favorite-filter" });

    let filter: ITreeItem | undefined = findNodeById(items, f.id);

    if (!filter) {
      const isFolder = scope.tab === FOLDERS_TAB_KEY;

      try {
        if (isFolder) {
          filter = await FoldersService.fetchFolder(scope, String(f.id));
        } else {
          const isAutoFilter = String(f.id).includes("/");
          const id = String(f.id).split("/")[0];
          const fetchedFilter = await FiltersService.fetchFilterFromServer(
            scope,
            id,
            isAutoFilter ? String(f.id) : "",
          );
          filter = isAutoFilter
            ? findNodeById(fetchedFilter.children, f.id)
            : fetchedFilter;
        }
      } catch (e) {
        console.error(`FavoritePanel->onFavoriteClick: ${e}`);
      }

      if (!filter && f.serverObject) {
        filter = { id: f.id, serverObject: f.serverObject } as ITreeItem;
      }
    }

    if (filter) {
      handleTreeItemClick(filter);
    }
  };

  const handleNodeRename = async (id: string, value: string): Promise<void> => {
    await FiltersService.rename(scope.projectId, id, value, items);
    await saveFavorites(favorites);
    filtersContext?.reloadItems(items);
    renameFavorite({ id, name: value });
    setEditableId(null);
  };

  const toggleFavoriteVisible = () => {
    setOpenState(!isOpen);
  };

  const handleCollapseAll = () => {
    const empty = new Set<string>();
    saveExpandedNodes(empty);
    setExpandedNodes(empty);
  };

  const resolveAutoFilterNode = useCallback(
    async (f: FavoriteItem): Promise<ITreeItem | undefined> => {
      const fromTree = findNodeById(items, f.id);
      if (fromTree) return fromTree;

      if (f.serverObject)
        return { id: f.id, serverObject: f.serverObject } as ITreeItem;

      const rootId = String(f.id).split("/")[0];
      const rootFilter = await FiltersService.fetchFilterFromServer(
        scope,
        rootId,
      );
      return findNodeById(rootFilter.children, f.id);
    },
    [scope, items],
  );

  const handleNodeExpand = useCallback(
    async (f: FavoriteItem) => {
      if (f.children?.length) return;
      try {
        const isFolder = scope.tab === FOLDERS_TAB_KEY;
        const isAutoFilterChild = String(f.id).includes("/");

        let children: ITreeItem[] | undefined;

        if (isFolder) {
          children = await FoldersService.getFolderChildren(
            scope,
            String(f.id),
          );
        } else if (isAutoFilterChild) {
          const treeNode = await resolveAutoFilterNode(f);
          if (treeNode) {
            const filter = await FiltersService.fetchAutoFilterChildren(
              scope,
              treeNode,
            );
            children = filter?.children;
          }
        } else {
          const filter = await FiltersService.fetchFilterFromServer(
            scope,
            String(f.id),
          );
          children = filter?.children;
        }

        if (!children?.length) {
          f.has_children = false;
          setVisibleItems((prev) => [...prev]);
          return;
        }
        f.children = children.map((child: ITreeItem) => ({
          id: child.id,
          name: child.name,
          ancestry: child.ancestry?.split("/") || [],
          has_children: child.extra?.hasChildren || child.children?.length > 0,
          serverObject: child.serverObject,
          parentId: String(f.id),
        }));
        setVisibleItems((prev) => [...prev]);
      } catch (e) {
        console.error(`FavoritePanel->handleNodeExpand: ${e}`);
      }
    },
    [scope, resolveAutoFilterNode],
  );

  const toggleNodeExpand = useCallback(
    async (nodeKey: string, f: FavoriteItem) => {
      await handleNodeExpand(f);
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        next.has(nodeKey) ? next.delete(nodeKey) : next.add(nodeKey);
        saveExpandedNodes(next);
        return next;
      });
    },
    [handleNodeExpand],
  );

  const renderFavoriteItem = (f: FavoriteItem, level: number, path: string) => {
    const hasChildren = f.has_children || (f.children?.length ?? 0) > 0;
    const nodeKey = `${path}/${f.id}`;
    const isExpanded = expandedNodes.has(nodeKey);

    return (
      <React.Fragment key={`fav_${nodeKey}`}>
        <ListItemRow
          data={f}
          isDataReady={false}
          paddingLeft={hasChildren ? level * 18 : level * 18 + 18}
          isSelected={f.id == selectedItem?.id}
          hasChildren={hasChildren}
          isEditable={editableId === String(f.id)}
          handleRename={async (value) => handleNodeRename(String(f.id), value)}
          scope={scope}
          criteria={criteria}
          isFavorite={true}
          handleNodeClicked={() => onFavoriteClick(f)}
          ContextMenu={
            settings?.enableSettings &&
            (level === 0 ||
              !String(f.id).includes("/") ||
              filtersContext?.copyAsPtql) ? (
              <FavoriteContextMenu
                favorite={f}
                scope={scope}
                items={items}
                setIsEditable={() => setEditableId(String(f.id))}
                // ToDo: fix typescript error
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                handleFavoriteClick={() => onFavoriteClick(f, true)}
              />
            ) : null
          }
          isTreeNodeExpand={isExpanded}
          onExpandStateChanged={
            hasChildren ? () => toggleNodeExpand(nodeKey, f) : undefined
          }
          type={ListTypes.FAVORITES}
          handleFavoriteClick={toggleFavorite}
        />
        {isExpanded &&
          f.children?.map((child) =>
            renderFavoriteItem(child, level + 1, nodeKey),
          )}
      </React.Fragment>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.caption}>
          <BareButton
            handleClick={toggleFavoriteVisible}
            className={isOpen ? styles["collapse-node"] : styles["expand-node"]}
          >
            <MuxToggle />
          </BareButton>
          {getTranslation("filters-tree.context-menu.favorites", "Favorites")}
        </div>
        <div>
          <CollapseTreeButton
            isDisabled={!expandedNodes.size}
            handleCollapseTree={handleCollapseAll}
          />
        </div>
      </div>
      <ul
        className={styles.favorites}
        ref={favoriteRef}
        style={!isOpen ? { height: "0px" } : {}}
      >
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <Loading size="modal" />
          </div>
        ) : error ? (
          <FilterListMessage message={error} />
        ) : (
          visibleItems.map((f) => renderFavoriteItem(f, 0, "root"))
        )}
      </ul>
    </div>
  );
};
